import { ExternalLink, Shield, TrendingUp, Clock, Info, GitCompare } from 'lucide-react';
import { useCompare } from '../../context/CompareContext';
import { useNavigate } from 'react-router-dom';
import { StakingOption } from '../../types';
import { useState } from 'react';
import { useAuthStore } from '../../store/authStore';

interface StakingCardProps {
  option: StakingOption;
}

export default function StakingCard({ option }: StakingCardProps) {
  const [showDetails, setShowDetails] = useState(false);
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const { addToCompare, removeFromCompare, isInCompare, compareList, maxCompare } = useCompare();
  const inCompare = isInCompare(String(option.id));
  const canAdd = compareList.length < maxCompare;

  const getRiskColor = (risk: string) => {
    switch ((risk || "medium").toLowerCase()) {
      case 'low': return 'text-green-400 bg-green-400/10 border-green-400/30';
      case 'medium': return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30';
      case 'high': return 'text-neon-red bg-neon-red/10 border-neon-red/30';
      default: return 'text-sky-blue bg-sky-blue/10 border-sky-blue/30';
    }
  };

  const getRiskIcon = (risk: string) => {
    switch ((risk || "medium").toLowerCase()) {
      case 'low': return '🟢';
      case 'medium': return '🟡';
      case 'high': return '🔴';
      default: return '⚪';
    }
  };

  const handleStakeClick = () => {
    const url = option.stakeUrl || (option.metadata as any)?.stakeUrl;
    if (url) {
      window.open(url, '_blank');
    }
  };

  return (
    <div className="card-premium group relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-cta opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
      
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            {option.logoUrl && (
              <img 
                src={option.logoUrl} 
                alt={option.name}
                className="w-12 h-12 rounded-lg"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            <div>
              <h3 className="text-xl font-bold text-cream group-hover:text-electric-blue transition-colors">
                {option.name}
              </h3>
              <p className="text-sm text-sky-blue">{option.symbol}</p>
            </div>
          </div>
          
          <div className="text-right">
            <div className="text-3xl font-bold gradient-text-neon">
              {option.apy}%
            </div>
            <div className="text-xs text-sky-blue">APY</div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="inline-block px-3 py-1 bg-steel-blue/20 border border-sky-blue/30 rounded-full text-xs text-sky-blue">
            {option.category}
          </span>
          {isAuthenticated && (option as any).tvl_tier && (
            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${
              (option as any).tvl_tier === 'Tier 1' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
              (option as any).tvl_tier === 'Tier 2' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
              (option as any).tvl_tier === 'Tier 3' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
              'bg-red-500/20 text-red-400 border-red-500/30'
            }`}>
              {(option as any).tvl_tier}
            </span>
          )}
          {isAuthenticated && (option as any).grade && (
            <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold border ${
              ((option as any).grade || '').charAt(0) === 'A' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
              ((option as any).grade || '').charAt(0) === 'B' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
              ((option as any).grade || '').charAt(0) === 'C' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
              'bg-orange-500/20 text-orange-400 border-orange-500/30'
            }`}>
              {(option as any).grade}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className={`p-3 rounded-lg border ${getRiskColor(option.riskLevel)}`}>
            <div className="flex items-center gap-1 mb-1">
              <Shield className="w-3 h-3" />
              <span className="text-xs opacity-80">Risk</span>
            </div>
            <div className="font-semibold text-sm flex items-center gap-1">
              <span>{getRiskIcon(option.riskLevel)}</span>
              <span className="capitalize">{option.riskLevel}</span>
            </div>
          </div>

          <div className="p-3 rounded-lg border bg-sky-blue/5 border-sky-blue/20">
            <div className="flex items-center gap-1 mb-1">
              <Clock className="w-3 h-3 text-sky-blue" />
              <span className="text-xs text-sky-blue/80">Lock Period</span>
            </div>
            <div className="font-semibold text-sm text-cream">
              {option.lockPeriod || 'Flexible'}
            </div>
          </div>
        </div>

        {option.description && (
          <p className="text-sm text-sky-blue/80 mb-4 line-clamp-2">
            {option.description}
          </p>
        )}

        {(option.minDeposit || option.rewards) && (
          <button onClick={() => setShowDetails(!showDetails)} className="w-full text-left text-sm text-sky-blue hover:text-cream transition-colors mb-3 flex items-center gap-1">
            <Info className="w-4 h-4" />
            <span>{showDetails ? 'Hide' : 'Show'} Details</span>
          </button>
        )}

        {showDetails && (
          <div className="bg-navy-dark/50 rounded-lg p-3 mb-4 space-y-2 text-sm border border-sky-blue/10">
            {option.minDeposit && (
              <div className="flex justify-between">
                <span className="text-sky-blue/80">Min Deposit:</span>
                <span className="text-cream font-medium">{option.minDeposit}</span>
              </div>
            )}
            {option.rewards && (
              <div className="flex justify-between">
                <span className="text-sky-blue/80">Rewards:</span>
                <span className="text-cream font-medium">{option.rewards}</span>
              </div>
            )}
            {option.network && (
              <div className="flex justify-between">
                <span className="text-sky-blue/80">Network:</span>
                <span className="text-cream font-medium">{option.network}</span>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={handleStakeClick} disabled={!option.stakeUrl && !(option.metadata as any)?.stakeUrl} className={`flex-1 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${(option.stakeUrl || (option.metadata as any)?.stakeUrl) ? 'bg-gradient-cta text-white hover:shadow-neon' : 'bg-steel-blue/20 text-sky-blue/50 cursor-not-allowed'}`}>
            <TrendingUp className="w-4 h-4" />
            <span>Stake Now</span>
          </button>

          <button
            onClick={() => inCompare ? removeFromCompare(String(option.id)) : addToCompare(option)}
            disabled={!inCompare && !canAdd}
            className={`px-4 py-3 rounded-lg border transition-all flex items-center justify-center ${inCompare ? 'border-neon-red text-neon-red bg-neon-red/10' : canAdd ? 'border-sky-blue/30 text-sky-blue hover:bg-sky-blue/10' : 'border-sky-blue/10 text-sky-blue/30 cursor-not-allowed'}`}
            title={inCompare ? 'Remove from compare' : 'Add to compare'}
          >
            <GitCompare className="w-4 h-4" />
          </button>
          <button onClick={() => navigate(`/protocol/${option.slug}`)} className="px-4 py-3 rounded-lg border border-sky-blue/30 text-sky-blue hover:bg-sky-blue/10 transition-all flex items-center justify-center" title="View details">
            <Info className="w-4 h-4" />
          </button>
          {option.website && (
            <a href={option.website} target="_blank" rel="noopener noreferrer" className="px-4 py-3 rounded-lg border border-sky-blue/30 text-sky-blue hover:bg-sky-blue/10 transition-all flex items-center justify-center" title="Visit website">
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>

        {option.audit && (
          <div className="mt-3 pt-3 border-t border-sky-blue/10 flex items-center gap-2 text-xs text-green-400">
            <Shield className="w-3 h-3" />
            <span>Audited by {option.audit}</span>
          </div>
        )}
      </div>
    </div>
  );
}
